// SPDX-License-Identifier: Apache-2.0

import { jsonMember, jsonObject } from "typedjson";
import { jsonBigIntMember } from "./bigint";
import { ChannelAsset, ChannelNervosAsset } from "./channelState";
import EthereumAsset from "./ethereumAsset";
import StellarAsset from "./stellarAsset";
import Address from "./address";
import { utils } from "ethers";

export type AssetType = "ETH" | "ERC20" | "native" | "credit" | "SUDT" | "CKB";
@jsonObject()
export class SUDT {
  @jsonMember(Number) public maxCapacity: number;
  @jsonMember(String) public codeHash: string;
  @jsonMember(String) public hashType: string;
  @jsonMember(String) public args: string;

  constructor(
    maxCapacity: number,
    codeHash: string,
    hashType: string,
    args: string,
  ) {
    this.maxCapacity = maxCapacity;
    this.codeHash = codeHash;
    this.hashType = hashType;
    this.args = args;
  }
}
@jsonObject()
export class NervosAsset {
  @jsonMember(String) public code: string;
  @jsonMember(String) public name: string;
  @jsonMember(Number) public decimal: number;
  @jsonMember(String) public type: AssetType;
  @jsonBigIntMember() public chainID: bigint;
  @jsonMember(SUDT) public address: SUDT;

  constructor(
    code: string,
    name: string,
    decimal: number,
    chainID: bigint,
    address: SUDT,
    type: AssetType,
  ) {
    this.code = code;
    this.name = name;
    this.decimal = decimal;
    this.chainID = chainID;
    this.address = address;
    this.type = type;
  }

  assetID = () => makeAssetID(this.address.args, this.chainID);

  get isSUDTToken() {
    return this.type === "SUDT";
  }

  toChannelAsset(): ChannelAsset {
    return new ChannelNervosAsset(this.address, this.chainID);
  }

  toNervosChannelAsset(): ChannelNervosAsset {
    return new ChannelNervosAsset(this.address, this.chainID);
  }
}

// The AssetID is the unique identifier for assets and is generated by the
// asset's address and chain ID of the chain it lives on.
export type AssetID = string;

export function makeAssetID(address: string, chainID: bigint): AssetID {
  return address + chainID.toString();
}

export type AnyAsset = EthereumAsset | StellarAsset | NervosAsset;

/**
 * Converts an asset to a plain JSON object suitable for JSON.stringify
 */
export function serializeAsset(asset: AnyAsset): any {
  if (asset instanceof EthereumAsset) {
    return {
      __type: "EthereumAsset",
      code: asset.code,
      name: asset.name,
      type: asset.type,
      chainID: asset.chainID.toString(), // bigint → string
      address: asset.address.toString(), // Address → string
      assetHolder: asset.assetHolder.toString(), // Address → string
    };
  } else if (asset instanceof StellarAsset) {
    return {
      __type: "StellarAsset",
      code: asset.code,
      name: asset.name,
      type: asset.type,
      contractID: asset.contractID,
      chainID: asset.chainID.toString(), // bigint → string
    };
  } else if (asset instanceof NervosAsset) {
    return {
      __type: "NervosAsset",
      code: asset.code,
      name: asset.name,
      decimal: asset.decimal,
      type: asset.type,
      chainID: asset.chainID.toString(), // bigint → string
      address: {
        maxCapacity: asset.address.maxCapacity,
        codeHash: asset.address.codeHash,
        hashType: asset.address.hashType,
        args: asset.address.args,
      } as SUDT,
    };
  } else {
    throw new Error("Unknown asset type");
  }
}

/**
 * Converts an array of assets to JSON string
 */
export function serializeAssets(assets: AnyAsset[]): string {
  if (!assets) return "[]";
  return JSON.stringify(assets.map(serializeAsset));
}

export function deserializeAsset(json: any): AnyAsset {
  switch (json.__type) {
    case "EthereumAsset":
      return new EthereumAsset(
        json.code,
        json.name,
        json.type,
        BigInt(json.chainID), // string → bigint
        new Address(utils.arrayify(json.address)), // restore Address
        new Address(utils.arrayify(json.assetHolder))
      );
    case "StellarAsset":
      return new StellarAsset(
        json.code,
        json.name,
        json.type,
        json.contractID,
        BigInt(json.chainID) // string → bigint
      );
    case "NervosAsset":
      const sudt = new SUDT(
        json.address.maxCapacity,
        json.address.codeHash,
        json.address.hashType,
        json.address.args
      );
      return new NervosAsset(
        json.code,
        json.name,
        json.decimal,
        BigInt(json.chainID),
        sudt,
        json.type
      );
    default:
      throw new Error("Unknown asset type: " + json.__type);
  }
}

/**
 * Converts JSON string back to assets array
 */
export function deserializeAssets(str: string): AnyAsset[] {
  if (!str) return [];
  const arr = JSON.parse(str);
  console.log("Deserialized assets array:", arr);
  return arr.map(deserializeAsset);
}
